import { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';

// Override Link to handle external URLs
const Link = ({ to, children, ...props }: any) => {
    if (typeof to === 'string' && to.startsWith('http')) {
        return (
            <a href={to} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
            </a>
        );
    }
    return (
        <RouterLink to={to} {...props}>
            {children}
        </RouterLink>
    );
};
import { Menu, X, ChevronDown, ChevronRight } from 'lucide-react';
import OptimizedImage from '../common/OptimizedImage';

interface NavItem {
    name: string;
    path: string;
    children?: NavItem[];
}

const renderItemLabel = (name: string, prefix?: string) => {
    // Single line display as per user request
    return (
        <span className="flex items-center text-left">
            {prefix && <span className="mr-1.5 shrink-0">{prefix}</span>}
            <span>{name}</span>
        </span>
    );
};

const DesktopMenuItem = ({ item }: { item: NavItem }) => {
    const [activeL2, setActiveL2] = useState<NavItem | null>(item.children ? item.children[0] : null);
    const [activeL3, setActiveL3] = useState<NavItem | null>(null);

    return (
        <div
            className="relative group h-full flex items-center"
            onMouseEnter={() => {
                if (item.children) {
                    setActiveL2(item.children[0]);
                    setActiveL3(null);
                }
            }}
        >
            <Link
                to={item.path}
                aria-haspopup={item.children ? "true" : "false"}
                aria-expanded="false"
                className="flex items-center text-[16px] font-medium tracking-wide transition-colors duration-200 relative text-[#212966] px-[5px] py-[20px] after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-0 after:h-[2px] after:bg-[#212966] after:transition-all after:duration-300 hover:after:w-full"
                aria-label={`${item.name} 메뉴`}
            >
                {item.name}
            </Link>

            {/* Mega Menu Dropdown */}
            {item.children && (
                <div className="absolute top-full left-0 pt-0 opacity-0 invisible -translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300 ease-out z-100">
                    <div className="bg-white shadow-[2px_2px_15px_rgba(0,0,0,0.1)] border-t border-gray-100 flex min-h-[550px] overflow-visible">
                        {/* Column 1: Level 2 Sidebar */}
                        <div className="min-w-[280px] w-max bg-white pt-6 pb-12 border-r border-gray-50">
                            <div className="px-6 mb-4">
                                <span className="text-[16px] font-bold text-[#0369a1]">{item.name}</span>
                                <div className="h-px bg-gray-200 mt-2 w-full" aria-hidden="true"></div>
                            </div>
                            <div className="flex flex-col">
                                {item.children.map((child) => (
                                    <div
                                        key={child.name}
                                        onMouseEnter={() => {
                                            setActiveL2(child);
                                            setActiveL3(null);
                                        }}
                                        className={`mega-menu-item group/side ${activeL2?.name === child.name ? 'mega-menu-active' : ''}`}
                                    >
                                        <Link to={child.path} className="text-[15px] flex-1" aria-label={`${child.name} 바로가기`}>
                                            {renderItemLabel(child.name)}
                                        </Link>
                                        {child.children && <ChevronRight size={14} className={`ml-2 transition-transform ${activeL2?.name === child.name ? 'translate-x-1' : 'opacity-50'}`} aria-hidden="true" />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Column 2: Level 3 List */}
                        {activeL2?.children && (
                            <div className="min-w-[360px] w-max max-w-[600px] bg-white pt-6 pb-12 border-r border-gray-50 animate-in fade-in slide-in-from-left-2 duration-200">
                                <div className="px-6 mb-4">
                                    <span className="text-[16px] font-bold text-[#0369a1]">{activeL2.name}</span>
                                    <div className="h-px bg-gray-200 mt-2 w-full" aria-hidden="true"></div>
                                </div>
                                <div className="flex flex-col">
                                    {activeL2.children.map((child) => (
                                        <div
                                            key={child.name}
                                            onMouseEnter={() => setActiveL3(child)}
                                            className={`mega-menu-item ${activeL3?.name === child.name ? 'mega-menu-active' : ''}`}
                                        >
                                            <Link to={child.path} className="text-[16px] flex-1" aria-label={`${child.name} 바로가기`}>
                                                {renderItemLabel(child.name)}
                                            </Link>
                                            {child.children && <ChevronRight size={12} className="ml-2" aria-hidden="true" />}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Column 3: Level 4 Flyout */}
                        {activeL3?.children && (
                            <div className="min-w-[360px] w-max max-w-[600px] bg-white pt-6 pb-12 border-r border-gray-50 shadow-lg animate-in fade-in slide-in-from-left-2 duration-200">
                                <div className="px-6 mb-4">
                                    <span className="text-[16px] font-bold text-[#0369a1]">{activeL3.name}</span>
                                    <div className="h-px bg-gray-200 mt-2 w-full" aria-hidden="true"></div>
                                </div>
                                <div className="flex flex-col">
                                    {activeL3.children.map((child) => (
                                        <div key={child.name} className="mega-menu-item">
                                            <Link
                                                to={child.path}
                                                className="text-[16px] w-full block"
                                                aria-label={`${child.name} 바로가기`}
                                            >
                                                {renderItemLabel(child.name, '•')}
                                            </Link>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Empty state removed as per user request */}
                    </div>
                </div>
            )}
        </div>
    );
};

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [expandedItem, setExpandedItem] = useState<string | null>(null);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navItems: NavItem[] = [
        { name: 'HOME', path: '/' },
        {
            name: 'Products',
            path: '/products/data-security',
            children: [
                {
                    name: '데이터 보안',
                    path: '/products/data-security',
                    children: [
                        {
                            name: '하드웨어 보안모듈(HSM)',
                            path: '/products/data-security/hsm',
                            children: [
                                { name: '하드웨어 보안모듈(HSM)', path: '/products/data-security/hsm' },
                                { name: 'Luna 네트워크 HSM', path: '/products/data-security/luna-network-hsm' },
                                { name: 'Luna PCIe HSM', path: '/products/data-security/luna-pcie-hsm' },
                                { name: 'Luna USB HSM', path: '/products/data-security/luna-usb-hsm' },
                                { name: 'Luna 백업 HSM', path: '/products/data-security/luna-backup-hsm' },
                                { name: 'ProtectServer HSM', path: '/products/data-security/protectserver-hsm' },
                                { name: 'Crypto Command Center', path: '/products/data-security/crypto-command-center' },
                            ]
                        },
                        {
                            name: '암호키 관리',
                            path: '/products/data-security/ciphertrust-manager',
                            children: [
                                { name: 'CipherTrust Manager', path: '/products/data-security/ciphertrust-manager' },
                                { name: 'CipherTrust Cloud Key Manager (CCKM)', path: '/products/data-security/ciphertrust-cloud-key-manager' },
                                { name: '이중키 암호화 (DKE)', path: '/products/data-security/double-key-encryption' },
                            ]
                        },
                        { name: 'CipherTrust Data Security Platform (CDSP)', path: '/products/data-security/ciphertrust-platform' },
                        { name: 'Data Security Posture Management (DSPM)', path: '/products/data-security/dspm' },
                        {
                            name: '데이터 암호화',
                            path: '/products/data-security/data-encryption',
                            children: [
                                { name: '데이터 암호화(Data-at-Rest Encryption)', path: '/products/data-security/data-encryption' },
                                { name: '투명 암호화 (CTE)', path: '/products/data-security/transparent-encryption' },
                                { name: 'Kubernetes 암호화 (CTE)', path: '/products/data-security/kubernetes-encryption' },
                                { name: 'UserSpace 암호화 (CTE-U)', path: '/products/data-security/userspace-encryption' },
                                { name: '애플리케이션 데이터 보호(CADP)', path: '/products/data-security/application-data-protection' },
                                { name: '토큰화 (Tokenization)', path: '/products/data-security/tokenization' },
                                { name: '데이터 보호 게이트웨이 (DPG)', path: '/products/data-security/data-protection-gateway' },
                                { name: '데이터베이스 보안 (CDP)', path: '/products/data-security/database-security' },
                                { name: '정적 데이터 마스킹', path: '/products/data-security/static-data-masking' },
                                { name: '지능형 데이터 보호', path: '/products/data-security/intelligent-data-protection' },
                                { name: '암호화 접근 방식 가이드', path: '/products/data-security/right-encryption-approach' },
                            ]
                        },
                        { name: '데이터 검색 및 분류 (DDC)', path: '/products/data-security/ddc' },
                        { name: '시크릿 관리 (Secrets)', path: '/products/data-security/secrets-management' },
                        { name: '고속 네트워크 암호화 (HSE)', path: '/products/data-security/high-speed-encryption' },
                        { name: 'DevSecOps 보안', path: '/products/data-security/devsecops' },
                        { name: 'Data Protection on Demand (DPoD)', path: '/products/data-security/dpod' },
                    ]
                },
                {
                    name: '어플리케이션 보안',
                    path: '/products/application-security',
                    children: [
                        { name: '어플리케이션 오버뷰', path: '/products/application-security' },
                        { name: 'AI 어플리케이션 보안', path: '/products/application-security/ai-application-security' },
                        { name: '웹 어플리케이션 방화벽', path: '/products/application-security/web-application-firewall-waf' },
                        { name: '지능형 봇 보호', path: '/products/application-security/advanced-bot-protection-management' },
                        { name: '통합 API 보안 플랫폼', path: '/products/application-security/api-security' },
                        { name: 'DDoS 보호', path: '/products/application-security/ddos-protection-services' },
                        { name: '클라이언트 측 보호', path: '/products/application-security/client-side-protection-csp' },
                        { name: '계정 탈취 보호', path: '/products/application-security/account-takeover-protection' },
                        { name: '콘텐츠 전송 네트워크', path: '/products/application-security/content-delivery-network-cdn' }
                    ]
                },
                {
                    name: '액세스 관리 및 인증',
                    path: '/products/access-management',
                    children: [
                        {
                            name: '액세스 관리',
                            path: '/products/access-management',
                            children: [
                                { name: '아이덴티티 및 액세스 관리(IAM)', path: '/products/access-management' },
                                { name: 'SafeNet Trusted Access (STA)', path: '/products/access-management/safenet-trusted-access' },
                                { name: 'Identity as a Service (IDaaS)', path: '/products/access-management/identity-as-a-service' },
                                { name: 'Authentication as a Service (AaaS)', path: '/products/access-management/authentication-as-a-service' },
                                { name: 'SAML Authentication', path: '/products/access-management/saml-authentication' },
                            ]
                        },
                        {
                            name: '신원 및 자격증명 관리',
                            path: '/products/access-management/workforce-identity',
                            children: [
                                { name: '직원 아이덴티티(Workforce Identity)', path: '/products/access-management/workforce-identity' },
                                { name: '고객 아이덴티티(Customer Identity, CIAM)', path: '/products/access-management/customer-identity' },
                                { name: 'B2B 아이덴티티(B2B Identity)', path: '/products/access-management/b2b-identity' },
                                { name: '긱 워커 아이덴티티(Gig Worker Identity)', path: '/products/access-management/gig-worker-identity' },
                            ]
                        },
                        {
                            name: '클라우드 SSO',
                            path: '/products/access-management/cloud-sso',
                            children: [
                                { name: '클라우드 SSO(Single Sign-On)', path: '/products/access-management/cloud-sso' },
                                { name: '신속한 SSO 통합(Rapid SSO Integrations)', path: '/products/access-management/cloud-sso/integrations' },
                            ]
                        },
                        {
                            name: '다중 인증 (MFA)',
                            path: '/products/access-management/multi-factor-authentication',
                            children: [
                                { name: '다중 인증 (MFA)', path: '/products/access-management/multi-factor-authentication' },
                                { name: '인증 수단 (Authenticators)', path: '/products/access-management/authenticators' },
                                { name: 'PKI USB 인증서', path: '/products/access-management/pki-usb-authentication' },
                                { name: '인증서 기반 스마트카드', path: '/products/access-management/smart-cards' },
                                { name: 'FIDO 인증 장치', path: '/products/access-management/fido-devices' },
                                { name: 'OTP 인증 장치', path: '/products/access-management/otp-tokens' },
                                { name: 'OneWelcome 플랫폼', path: '/products/access-management/onewelcome-platform' },
                                { name: '모바일 인증 (MobilePASS+)', path: '/products/access-management/mobilepass-otp' },
                                { name: '온프레미스 인증 관리', path: '/products/access-management/on-prem-authentication-management' },
                                { name: '보안 애플리케이션', path: '/products/access-management/security-applications' },
                                { name: '패스워드리스 인증', path: '/products/access-management/passwordless-authentication' },
                                { name: '스마트카드 리더', path: '/products/access-management/smart-card-readers' },
                            ]
                        },
                    ]
                },
                {
                    name: '서버 가상화',
                    path: '/products/virtualization',
                    children: [
                        { name: 'Proxmox 가상화(Proxmox Virtualization)', path: '/products/virtualization/proxmox' },
                        { name: 'VMware 가상화(VMware Virtualization)', path: '/products/virtualization/vmware' },
                    ]
                },
                {
                    name: '스토리지 가상화',
                    path: '/products/storage-virtualization',
                    children: [
                        { name: 'StorSafe VTL 및 중복 제거(FalconStor StorSafe, VTL & Deduplication)', path: '/products/virtualization/storsafe' },
                        { name: 'StorGuard NSS/CDP 및 복제(FalconStor StorGuard, NSS/CDP & Replication)', path: '/products/virtualization/storguard' },
                        { name: 'StorSight 통합 관리(FalconStor StorSight, Centralized Management)', path: '/products/virtualization/storsight' },
                    ]
                },

                {
                    name: '백업 솔루션',
                    path: '/products/backup',
                    children: [
                        { name: 'Veeam Data Protection', path: '/products/backup/veeam' },
                        { name: 'LG U+ Managed Backup Service', path: '/products/backup/managed' },
                    ]
                },
                {
                    name: 'IT 인프라',
                    path: '/products/infrastructure'
                },
            ]
        },
        {
            name: 'Solutions',
            path: '/solutions/overview',
            children: [
                { name: '솔루션 개요', path: '/solutions/overview' },
                {
                    name: '사용 사례별 (By Use Case)',
                    path: '/solutions/use-case',
                    children: [
                        { name: 'AI 보안', path: '/solutions/use-case/ai-security' },
                        { name: '5G 보안', path: '/solutions/use-case/5g-security' },
                        {
                            name: '액세스 보안',
                            path: '/solutions/use-case/access-security',
                            children: [
                                { name: '원격 액세스 보안', path: '/solutions/use-case/secure-remote-access' },
                                { name: 'VPN 보안', path: '/solutions/use-case/secure-vpn-access' },
                                { name: '클라우드 액세스 보안', path: '/solutions/use-case/secure-cloud-access' },
                                { name: 'VDI 보안', path: '/solutions/use-case/vdi-security-solution' },
                                { name: '2팩터 인증 (2FA)', path: '/solutions/use-case/two-factor-authentication' },
                                { name: '웹 및 클라우드 SSO', path: '/solutions/use-case/web-and-cloud-sso' },
                                { name: '물리 및 논리 액세스 관리', path: '/solutions/use-case/physical-and-logical-access-control' },
                                { name: '사이버 보험 MFA', path: '/solutions/use-case/mfa-for-cyber-insurance' },
                            ]
                        },
                        { name: '블록체인', path: '/solutions/use-case/blockchain' },
                        {
                            name: '클라우드 보안',
                            path: '/solutions/use-case/cloud-security',
                            children: [
                                { name: 'Amazon Web Services (AWS)', path: '/solutions/use-case/aws-security' },
                                { name: 'BYOE (사전 암호화)', path: '/solutions/use-case/byoe' },
                                { name: '클라우드 데이터 암호화', path: '/solutions/use-case/cloud-data-encryption' },
                                { name: '클라우드 SSO', path: '/solutions/use-case/web-and-cloud-sso' },
                                { name: 'Google Cloud Platform', path: '/solutions/use-case/gcp-security' },
                                { name: '하이브리드/온프레미스 HSM', path: '/solutions/use-case/hybrid-hsm' },
                                { name: 'Microsoft Azure', path: '/solutions/use-case/azure-security' },
                                { name: 'Microsoft 이중 키 암호화 (DKE)', path: '/solutions/use-case/microsoft-dke' },
                                { name: '멀티 클라우드 통합 관리', path: '/solutions/use-case/multi-cloud-management' },
                                { name: 'Oracle Cloud Infrastructure', path: '/solutions/use-case/oracle-cloud-security' },
                            ]
                        },
                        {
                            name: '데이터 보안 및 암호화',
                            path: '/solutions/use-case/data-security',
                            children: [
                                { name: '지능형 지속 위협(APT)', path: '/solutions/use-case/apt-prevention' },
                                { name: '빅데이터 보안', path: '/solutions/use-case/big-data-security' },
                                { name: '데이터베이스 보안 및 암호화', path: '/solutions/use-case/database-security' },
                                { name: 'DevSecOps', path: '/solutions/use-case/devsecops-security' },
                                { name: 'Docker 암호화', path: '/solutions/use-case/docker-security' },
                                { name: 'Kubernetes용 암호화', path: '/solutions/use-case/kubernetes-security' },
                                { name: 'UserSpace 암호화', path: '/solutions/use-case/userspace-encryption' },
                                { name: 'MongoDB 암호화', path: '/solutions/use-case/mongodb-encryption' },
                                { name: 'MS SQL 서버 암호화', path: '/solutions/use-case/mssql-security' },
                                { name: 'NoSQL 암호화', path: '/solutions/use-case/nosql-security' },
                                { name: 'Oracle 데이터베이스 암호화', path: '/solutions/use-case/oracle-security' },
                                { name: 'PostgreSQL 데이터베이스 암호화', path: '/solutions/use-case/postgresql-security' },
                                { name: 'SAP 데이터 보안', path: '/solutions/use-case/sap-security' },
                                { name: '비정형 파일 보안', path: '/solutions/use-case/unstructured-data-security' },
                                { name: 'TDE 키 관리', path: '/solutions/use-case/tde-key-management' },
                                { name: '가상 환경', path: '/solutions/use-case/virtual-security' },
                            ]
                        },
                        { name: '데이터 주권', path: '/solutions/use-case/data-sovereignty' },
                        { name: '디지털 전환', path: '/solutions/use-case/digital-transformation' },
                        {
                            name: 'IoT 보안',
                            path: '/solutions/use-case/iot-security',
                            children: [
                                { name: '스마트 그리드 보안', path: '/solutions/use-case/smart-grid-security' },
                                { name: '커넥티드 카 (IoT)', path: '/solutions/use-case/connected-cars' },
                                { name: '스마트 제조 (IoT)', path: '/solutions/use-case/secure-manufacturing' },
                                { name: '헬스케어 (IoT)', path: '/solutions/use-case/healthcare-security' },
                            ]
                        },
                        { name: 'PKI 자격 증명 관리', path: '/solutions/use-case/pki-identity' },
                        { name: 'PKI 보안 솔루션', path: '/solutions/use-case/pki-solutions' },
                        { name: '양자 (Quantum)', path: '/solutions/use-case/quantum' },
                        { name: '랜섬웨어 솔루션', path: '/solutions/use-case/ransomware' },
                        { name: '제로 트러스트 보안', path: '/solutions/use-case/zero-trust' },
                    ]
                },
                {
                    name: '산업별',
                    path: '/solutions/industry',
                    children: [
                        { name: '산업별 개요', path: '/solutions/industry' },
                        { name: '자동차 (Automotive)', path: '/solutions/industry/automotive' },
                        { name: '중요 인프라 (Critical Infrastructure)', path: '/solutions/industry/critical-infrastructure' },
                        { name: '교육 (Education)', path: '/solutions/industry/education' },
                        {
                            name: '정부 (Government)',
                            path: '/solutions/industry/government',
                            children: [
                                { name: '연방 정부', path: '/solutions/industry/government/federal-government' },
                                { name: '주 및 지방 정부', path: '/solutions/industry/government/state-local-government' },
                                { name: '법 집행 기관', path: '/solutions/industry/government/law-enforcement' },
                            ]
                        },
                        { name: '금융 서비스 (Financial Services)', path: '/solutions/industry/financial' },
                        { name: '의료 (Healthcare)', path: '/solutions/industry/healthcare' },
                        { name: '보험 (Insurance Providers)', path: '/solutions/industry/insurance' },
                        { name: '제조 및 산업 (Manufacturing and Industrial)', path: '/solutions/industry/manufacturing' },
                        { name: '미디어 및 엔터테인먼트 (Media and Entertainment)', path: '/solutions/industry/media-entertainment' },
                        { name: 'SaaS 제공업체 (SaaS Providers and Consumers)', path: '/solutions/industry/saas-providers' },
                        { name: '소매 및 이커머스 (Retail)', path: '/solutions/industry/retail' },
                        { name: '통신 (Telecommunications)', path: '/solutions/industry/telecom' },
                    ]
                },
                {
                    name: '규정준수',
                    path: '/solutions/compliance',
                    children: [
                        { name: '규정준수 개요', path: '/solutions/compliance/overview' },
                        { name: '한국 ISMS-P', path: '/solutions/compliance/isms-p' },
                        { name: '한국 PIPA', path: '/solutions/compliance/pipa' },
                        { name: 'GDPR', path: '/solutions/compliance/gdpr' },
                        { name: 'PCI DSS', path: '/solutions/compliance/pci-dss' },
                        { name: '데이터 주권', path: '/solutions/compliance/data-sovereignty' },
                        { name: 'eIDAS', path: '/solutions/compliance/eidas' },
                        { name: 'FIPS 및 CC 인증', path: '/solutions/compliance/fips-cc' },
                        { name: 'HIPAA', path: '/solutions/compliance/hipaa' },
                    ]
                },
            ]
        },
        {
            name: 'About',
            path: '/about/overview',
            children: [
                { name: '회사 개요', path: '/about/overview' },
                { name: '인사말', path: '/about/greetings' },
                { name: '조직도', path: '/about/org-chart' },
                { name: '주요연혁', path: '/about/history' },
                { name: '주요고객사', path: '/about/customers' },
                { name: '오시는길', path: '/about/location' },
                { name: '채용정보', path: '/about/career' },
            ]
        },
        { name: 'Contact', path: '/about/location' },
    ];

    const mainItems = navItems.filter(item => ['Products', 'Solutions', 'About'].includes(item.name));
    const rightItems = navItems.filter(item => ['Contact'].includes(item.name));

    return (
        <nav aria-label="메인 네비게이션" className={`fixed w-full z-50 bg-white border-b border-gray-100 transition-all duration-300 ${scrolled ? 'shadow-[0_2px_8px_0_rgba(0,0,0,0.1)] h-[60px]' : 'h-[70px]'}`}>
            <div className="container mx-auto px-4 h-full flex justify-between items-center text-[#212966]">
                <div className="flex items-center h-full">
                    <Link to="/" className="flex items-center mr-16" aria-label="ENTER IT 메인 페이지">
                        <OptimizedImage
                            src="/imgs/company/LOGO.webp"
                            webpSrcSet="/imgs/company/LOGO.webp 1x, /imgs/company/LOGO@2x.webp 2x"
                            alt="ENTER IT"
                            width="268"
                            height="40"
                            className="max-h-9 h-auto object-contain"
                        />
                    </Link>

                    {/* Desktop Main Menu */}
                    <div className="hidden lg:flex items-center h-full gap-8">
                        {mainItems.map((item) => (
                            <DesktopMenuItem key={item.name} item={item} />
                        ))}
                    </div>
                </div>

                <div className="hidden lg:flex items-center gap-6">
                    {rightItems.map((item) => {
                        if (item.name === 'Contact') {
                            return (
                                <Link
                                    key={item.name}
                                    to={item.path}
                                    className="bg-linear-to-r from-[#4eb2ca] to-[#1f6ea9] text-white px-6 py-2.5 font-bold hover:opacity-90 transition-opacity text-[15px]"
                                    aria-label="연락하기"
                                >
                                    연락하기
                                </Link>
                            );
                        }
                        return (
                            <DesktopMenuItem key={item.name} item={item} />
                        );
                    })}
                </div>

                {/* Mobile menu button */}
                <div className="lg:hidden">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="text-[#212966]"
                        aria-label={isOpen ? "메뉴 닫기" : "메뉴 열기"}
                        aria-expanded={isOpen}
                    >
                        {isOpen ? <X size={28} /> : <Menu size={28} />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isOpen && (
                <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl overflow-y-auto max-h-screen">
                    <div className="px-4 pt-2 pb-10 space-y-1">
                        {navItems.map((item) => (
                            <div key={item.name} className="py-2">
                                <div className="flex justify-between items-center border-b border-gray-50 pb-2">
                                    <Link
                                        to={item.path}
                                        onClick={() => setIsOpen(false)}
                                        className="text-lg font-bold text-[#212966]"
                                        aria-label={`${item.name} 메뉴 바로가기`}
                                    >
                                        {item.name}
                                    </Link>
                                    {item.children && (
                                        <button
                                            onClick={() => setExpandedItem(expandedItem === item.name ? null : item.name)}
                                            className="p-2 text-gray-600"
                                            aria-label={`${item.name} 하위 메뉴 ${expandedItem === item.name ? '접기' : '펼치기'}`}
                                            aria-expanded={expandedItem === item.name}
                                        >
                                            <ChevronDown size={20} className={`transition-transform duration-300 ${expandedItem === item.name ? 'rotate-180' : ''}`} />
                                        </button>
                                    )}
                                </div>

                                {item.children && expandedItem === item.name && (
                                    <div className="pl-4 mt-3 space-y-4 border-l-2 border-blue-50 ml-1">
                                        {item.children.map((child) => (
                                            <div key={child.name}>
                                                <Link
                                                    to={child.path}
                                                    onClick={() => setIsOpen(false)}
                                                    className="block text-sm font-bold text-gray-700 hover:text-[#212966] mb-2"
                                                    aria-label={`${child.name} 메뉴 바로가기`}
                                                >
                                                    {renderItemLabel(child.name)}
                                                </Link>
                                                {child.children && (
                                                    <div className="pl-4 space-y-4 flex flex-col mt-2">
                                                        {child.children.map((grandChild) => (
                                                            <div key={grandChild.name}>
                                                                <Link
                                                                    to={grandChild.path}
                                                                    onClick={() => setIsOpen(false)}
                                                                    className={`text-xs hover:text-[#212966] py-1 flex items-center ${grandChild.children ? 'font-bold text-gray-700' : 'text-gray-600'}`}
                                                                    aria-label={`${grandChild.name} 바로가기`}
                                                                >
                                                                    {renderItemLabel(grandChild.name, '•')}
                                                                </Link>
                                                                {grandChild.children && (
                                                                    <div className="pl-4 mt-1 space-y-1 flex flex-col border-l border-gray-200 ml-1">
                                                                        {grandChild.children.map((subItem) => (
                                                                            <Link
                                                                                key={subItem.name}
                                                                                to={subItem.path}
                                                                                onClick={() => setIsOpen(false)}
                                                                                className="text-[11px] text-gray-800 font-medium hover:text-[#212966] py-1"
                                                                                aria-label={`${subItem.name} 바로가기`}
                                                                            >
                                                                                {renderItemLabel(subItem.name, '-')}
                                                                            </Link>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
